/**
 * Dependency-free stack-trace parser.
 *
 * Turns a raw `Error.stack` string into structured frames. Handles the two
 * shapes we see across our supported browsers:
 *   - V8 / Chrome / Edge:  "    at fnName (file:line:col)"  /  "    at file:line:col"
 *   - Gecko / Safari:      "fnName@file:line:col"           /  "@file:line:col"
 *
 * Lines that don't carry a usable location (e.g. the leading message line, or
 * "at <anonymous>") are skipped. This is intentionally small and is covered by
 * fixture tests in __tests__/stack-parser.test.ts.
 */

export interface StackFrame {
    function: string | null;
    file: string | null;
    line: number | null;
    column: number | null;
}

const CHROME_WITH_FN = /^\s*at\s+(.+?)\s+\((.+?):(\d+):(\d+)\)\s*$/;
const CHROME_NO_FN = /^\s*at\s+(.+?):(\d+):(\d+)\s*$/;
const GECKO = /^\s*(.*?)@(.+?):(\d+)(?::(\d+))?\s*$/;

function parseChromeLine(line: string): StackFrame | null {
    let m = CHROME_WITH_FN.exec(line);
    if (m) {
        return { function: m[1], file: m[2], line: Number(m[3]), column: Number(m[4]) };
    }
    m = CHROME_NO_FN.exec(line);
    if (m) {
        return { function: null, file: m[1], line: Number(m[2]), column: Number(m[3]) };
    }
    return null;
}

function parseGeckoLine(line: string): StackFrame | null {
    const m = GECKO.exec(line);
    if (m) {
        return {
            function: m[1] ? m[1] : null,
            file: m[2],
            line: Number(m[3]),
            column: m[4] ? Number(m[4]) : null,
        };
    }
    return null;
}

/**
 * Parse an Error.stack string into frames. Returns [] for missing/empty input.
 */
export function parseStack(stack: string | null | undefined): StackFrame[] {
    if (!stack || typeof stack !== 'string') {
        return [];
    }

    const frames: StackFrame[] = [];
    for (const rawLine of stack.split('\n')) {
        if (!rawLine.trim()) {
            continue;
        }
        const frame = parseChromeLine(rawLine) || parseGeckoLine(rawLine);
        if (frame) {
            frames.push(frame);
        }
    }
    return frames;
}
